<template>
    <div class="bg-white w-100 rounded-4 border config-section kb-crawl-panel">
        <h2
            class="px-4 py-3 section-heading d-flex justify-content-between align-items-center"
            @click="expanded = !expanded"
        >
            <span>Website Knowledge Base</span>
            <span class="toggle-icon">{{ expanded ? '−' : '+' }}</span>
        </h2>

        <div class="p-4" v-show="expanded">
            <p class="text-muted mb-3">
                Crawls every page on your company website, structures the content with AI, and
                indexes it as company context so the chatbot can answer questions about anything
                published on your site. Each saved page is editable below.
            </p>

            <!-- Schedule -->
            <div class="schedule-row mb-4">
                <div class="schedule-label">
                    <strong>Refresh Schedule</strong>
                    <small class="text-muted d-block">How often the website should be re-crawled.</small>
                </div>
                <span class="schedule-value">Quarterly</span>
            </div>

            <!-- Action row -->
            <div class="d-flex flex-wrap align-items-center gap-3 mb-3">
                <button
                    class="btn btn-primary"
                    :disabled="isRunning || initialLoading"
                    @click="startCrawl"
                >
                    <span v-if="isRunning" class="spinner-border spinner-border-sm me-2" />
                    {{ isRunning ? runningLabel : actionLabel }}
                </button>
                <span v-if="!isRunning && pages.length" class="kb-stat">
                    <strong>{{ pages.length }}</strong> page{{ pages.length === 1 ? '' : 's' }} saved
                </span>
                <span v-if="lastSavedDisplay && !isRunning" class="text-muted small">
                    Last saved: {{ lastSavedDisplay }}
                </span>
            </div>

            <!-- Job status banner -->
            <div v-if="isRunning" class="status-banner running mb-3">
                <div class="status-headline">
                    <span class="loader-dot" />
                    Scraping your website…
                </div>
                <div class="status-progress">
                    Processed <strong>{{ jobProgress.processed }}</strong>
                    of <strong>{{ Math.max(jobProgress.discovered, jobProgress.processed) }}</strong>
                    discovered pages · saved <strong>{{ jobProgress.saved }}</strong>
                    <span v-if="jobProgress.failed">· skipped {{ jobProgress.failed }}</span>
                </div>
                <div class="progress-bar-track">
                    <div class="progress-bar-fill" :style="{ width: progressPct + '%' }" />
                </div>
            </div>

            <div v-else-if="completedSummary" class="status-banner done mb-3">
                <div class="status-headline">
                    <span class="check">✓</span>
                    Website crawl complete — {{ completedSummary.saved }} pages saved.
                </div>
                <div class="status-progress small text-muted">
                    Discovered {{ completedSummary.discovered }} ·
                    processed {{ completedSummary.processed }} ·
                    skipped {{ completedSummary.failed || 0 }}
                </div>
            </div>

            <div v-else-if="errorMessage" class="alert alert-danger py-2">
                {{ errorMessage }}
            </div>

            <!-- Page list (editable accordion) -->
            <div v-if="initialLoading" class="text-muted small mb-3">Loading saved pages…</div>

            <div v-else-if="pages.length === 0 && !isRunning" class="text-muted small mb-3">
                No pages saved yet. Click <strong>Get Data Now</strong> to crawl your website.
            </div>

            <!-- Search (only shown when there are pages to search) -->
            <div v-if="pages.length" class="kb-search-row mb-3">
                <div class="kb-search-input-wrapper">
                    <span class="kb-search-icon">🔍</span>
                    <input
                        type="text"
                        class="form-control kb-search-input"
                        v-model="searchQuery"
                        placeholder="Search by title, URL, topic, summary, or key facts…"
                    />
                    <button
                        v-if="searchQuery"
                        class="kb-search-clear"
                        @click="searchQuery = ''"
                        title="Clear search"
                    >×</button>
                </div>
                <span class="kb-search-count text-muted small" v-if="searchQuery">
                    {{ filteredPages.length }} of {{ pages.length }} match
                </span>
            </div>

            <div
                v-if="pages.length && !filteredPages.length"
                class="text-muted small mb-3 text-center py-3"
            >
                No pages match "<strong>{{ searchQuery }}</strong>".
            </div>

            <div class="accordion" v-if="filteredPages.length">
                <div
                    v-for="(page) in filteredPages"
                    :key="page.id"
                    class="accordion-item mb-2 border rounded"
                    :class="{ 'just-arrived': recentlyAdded[page.id] }"
                >
                    <button
                        class="accordion-header"
                        @click="toggleItem(page.id)"
                        :aria-expanded="openItems[page.id] ? 'true' : 'false'"
                    >
                        <span class="accordion-toggle">{{ openItems[page.id] ? '−' : '+' }}</span>
                        <span class="accordion-title">
                            <strong>{{ page.title || page.url }}</strong>
                            <span class="accordion-url small text-muted d-block">{{ page.url }}</span>
                        </span>
                        <span class="topic-pill" v-if="page.topic">{{ page.topic }}</span>
                        <span class="chunks-pill" v-if="page.chunksIndexed">
                            {{ page.chunksIndexed }} chunks
                        </span>
                    </button>

                    <div class="accordion-body p-3" v-show="openItems[page.id]">
                        <div class="form-row">
                            <label class="form-label">Title</label>
                            <input class="form-control" v-model="page.title" />
                        </div>
                        <div class="form-row">
                            <label class="form-label">URL</label>
                            <input class="form-control" :value="page.url" readonly />
                        </div>
                        <div class="form-row">
                            <label class="form-label">Topic</label>
                            <input class="form-control" v-model="page.topic" />
                        </div>
                        <div class="form-row">
                            <label class="form-label">Summary</label>
                            <textarea class="form-control" rows="3" v-model="page.summary" />
                        </div>
                        <div class="form-row">
                            <label class="form-label">Key Facts</label>
                            <div v-for="(fact, fi) in page.keyFacts" :key="fi" class="key-fact-row">
                                <input class="form-control" v-model="page.keyFacts[fi]" />
                                <button class="btn-icon" @click="removeKeyFact(page, fi)" title="Remove">×</button>
                            </div>
                            <button class="btn btn-outline-secondary btn-sm" @click="addKeyFact(page)">+ Add Fact</button>
                        </div>
                        <div class="form-row">
                            <label class="form-label">Content</label>
                            <textarea class="form-control" rows="8" v-model="page.content" />
                        </div>

                        <div class="d-flex justify-content-between align-items-center mt-3">
                            <small class="text-muted" v-if="page.updatedAt">
                                Last updated {{ formatDate(page.updatedAt) }}
                            </small>
                            <div class="d-flex gap-2">
                                <button
                                    class="btn btn-outline-danger btn-sm"
                                    :disabled="page._saving || page._deleting"
                                    @click="deletePage(page)"
                                >{{ page._deleting ? 'Removing…' : 'Delete' }}</button>
                                <button
                                    class="btn btn-primary btn-sm"
                                    :disabled="page._saving || page._deleting"
                                    @click="savePage(page)"
                                >{{ page._saving ? 'Saving…' : 'Save Edits' }}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    const POLL_INTERVAL_MS = 2000;
    // Friendly rotating status messages while crawling — keeps the UI alive
    // without spamming the user with API call counts.
    const RUNNING_LABELS = [
        'Fetching pages from your website…',
        'Reading and structuring content with AI…',
        'Indexing for chatbot retrieval…',
        'Almost done — finishing up…',
    ];

    export default {
        name: 'KbCrawlPanel',
        props: {
            scrapeFrequency: { type: String, default: 'quarterly' },
            lastScrapedAt:   { type: [String, Date, null], default: null },
        },
        data() {
            return {
                expanded: true,
                initialLoading: true,
                isRunning: false,
                errorMessage: '',
                // Schedule is locked to 'quarterly' on the UI side — the
                // dropdown was removed in favor of a static label. Whatever
                // value MySQL has, the crawl always sends 'quarterly'.
                localFrequency: 'quarterly',
                localLastScrapedAt: this.lastScrapedAt || null,

                // Saved pages (editable). Keyed by id; rendered in id order.
                pages: [],
                openItems: {},
                recentlyAdded: {},
                searchQuery: '',

                // Job state
                jobId: null,
                pollHandle: null,
                runningLabelIdx: 0,
                runningLabelHandle: null,
                jobProgress: { discovered: 0, processed: 0, saved: 0, failed: 0 },
                lastPolledPageId: 0,
                completedSummary: null,
            };
        },
        computed: {
            runningLabel() {
                return RUNNING_LABELS[this.runningLabelIdx % RUNNING_LABELS.length];
            },
            progressPct() {
                const d = Math.max(this.jobProgress.discovered, this.jobProgress.processed, 1);
                return Math.min(100, Math.round((this.jobProgress.processed / d) * 100));
            },
            lastSavedDisplay() {
                if (!this.localLastScrapedAt) return '';
                const d = new Date(this.localLastScrapedAt);
                if (isNaN(d.getTime())) return '';
                return d.toLocaleString();
            },
            // Button label adapts to whether the company already has crawled
            // pages: a fresh chatbot says "Get Data Now"; an existing one
            // says "Refresh Knowledge Base" so admins know they're re-running
            // a crawl that will replace prior content.
            actionLabel() {
                return this.pages.length > 0 ? 'Refresh Knowledge Base' : 'Get Data Now';
            },
            filteredPages() {
                const q = (this.searchQuery || '').trim().toLowerCase();
                if (!q) return this.pages;
                return this.pages.filter(p => {
                    const facts = Array.isArray(p.keyFacts) ? p.keyFacts.join(' ') : '';
                    const haystack = [
                        p.title, p.url, p.topic, p.summary, p.content, facts,
                    ].filter(Boolean).join(' ').toLowerCase();
                    return haystack.includes(q);
                });
            },
        },
        watch: {
            // Schedule is fixed at 'quarterly' — ignore prop changes.
            lastScrapedAt(v)   { this.localLastScrapedAt = v; },
        },
        async created() {
            await Promise.all([this.loadPages(), this.resumeIfRunning()]);
            this.initialLoading = false;
        },
        beforeDestroy() {
            this.stopPolling();
        },
        methods: {
            // ── Page list ───────────────────────────────────────────────────
            async loadPages() {
                try {
                    const response = await this.$api.get('/kb-crawl/pages');
                    const list = (response.data && response.data.data) || [];
                    this.pages = list.map(p => this.normalizePage(p));
                    this.lastPolledPageId = this.pages.length
                        ? Math.max(...this.pages.map(p => p.id))
                        : 0;
                } catch (err) {
                    // Non-fatal; keep the panel visible so user can retry crawl.
                }
            },
            normalizePage(p) {
                return {
                    ...p,
                    keyFacts: Array.isArray(p.keyFacts) ? p.keyFacts.slice() : [],
                    _saving: false,
                    _deleting: false,
                };
            },
            mergeIncomingPages(incoming) {
                if (!incoming || !incoming.length) return;
                const byId = new Map(this.pages.map(p => [p.id, p]));
                for (const np of incoming) {
                    const norm = this.normalizePage(np);
                    if (byId.has(np.id)) {
                        // Replace in place so editor state for OTHER rows is preserved.
                        const idx = this.pages.findIndex(p => p.id === np.id);
                        if (idx !== -1) this.$set(this.pages, idx, norm);
                    } else {
                        this.pages.push(norm);
                        this.$set(this.recentlyAdded, np.id, true);
                        setTimeout(() => this.$delete(this.recentlyAdded, np.id), 2500);
                    }
                    this.lastPolledPageId = Math.max(this.lastPolledPageId, np.id);
                }
                this.pages.sort((a, b) => a.id - b.id);
            },
            toggleItem(id) {
                this.$set(this.openItems, id, !this.openItems[id]);
            },
            addKeyFact(page)        { page.keyFacts.push(''); },
            removeKeyFact(page, fi) { page.keyFacts.splice(fi, 1); },

            // ── Schedule ────────────────────────────────────────────────────
            async saveSchedule() {
                try {
                    await this.$api.patch('/kb-crawl/schedule', {
                        scrapeFrequency: this.localFrequency,
                    });
                    this.$emit('saved', { scrapeFrequency: this.localFrequency });
                } catch (err) {
                    this.$toasted.error('Could not update refresh schedule.');
                }
            },

            // ── Crawl lifecycle ─────────────────────────────────────────────
            async resumeIfRunning() {
                try {
                    const response = await this.$api.get('/kb-crawl/jobs/latest');
                    const latest = response.data && response.data.data;
                    if (latest && (latest.status === 'queued' || latest.status === 'running')) {
                        this.jobId = latest.id;
                        this.startPolling();
                    }
                } catch (_) {
                    // Best-effort resume — first-time loads with no prior job land here.
                }
            },
            async startCrawl() {
                this.errorMessage = '';
                this.completedSummary = null;
                this.jobProgress = { discovered: 0, processed: 0, saved: 0, failed: 0 };
                try {
                    const response = await this.$api.post('/kb-crawl/run', {
                        scrapeFrequency: this.localFrequency,
                    });
                    const data = response.data && response.data.data;
                    if (!data || !data.jobId) {
                        throw new Error('Server did not return a job id.');
                    }
                    this.jobId = data.jobId;
                    this.startPolling();
                } catch (err) {
                    const msg = err && err.response && err.response.data && err.response.data.message;
                    this.errorMessage = msg || (err && err.message) || 'Could not start crawl';
                    this.$toasted.error(this.errorMessage);
                }
            },
            startPolling() {
                this.isRunning = true;
                this.lastPolledPageId = this.pages.length
                    ? Math.max(...this.pages.map(p => p.id))
                    : 0;
                this.runningLabelIdx = 0;
                this.runningLabelHandle = setInterval(() => {
                    this.runningLabelIdx++;
                }, 6000);
                this.pollOnce();
                this.pollHandle = setInterval(() => this.pollOnce(), POLL_INTERVAL_MS);
            },
            stopPolling() {
                if (this.pollHandle) clearInterval(this.pollHandle);
                if (this.runningLabelHandle) clearInterval(this.runningLabelHandle);
                this.pollHandle = null;
                this.runningLabelHandle = null;
            },
            async pollOnce() {
                if (!this.jobId) return;
                try {
                    const response = await this.$api.get(`/kb-crawl/jobs/${this.jobId}`, {
                        params: { afterPageId: this.lastPolledPageId },
                    });
                    const job = response.data && response.data.data;
                    if (!job) return;

                    this.jobProgress = {
                        discovered: job.pagesDiscovered || 0,
                        processed:  job.pagesProcessed  || 0,
                        saved:      job.pagesSaved      || 0,
                        failed:     job.pagesFailed     || 0,
                    };
                    if (job.pages && job.pages.length) {
                        this.mergeIncomingPages(job.pages);
                    }
                    if (job.status === 'done' || job.status === 'error' || job.status === 'cancelled') {
                        this.finishCrawl(job);
                    }
                } catch (err) {
                    // Transient network failure — keep polling.
                }
            },
            async finishCrawl(job) {
                this.stopPolling();
                this.isRunning = false;
                if (job.status === 'done') {
                    this.completedSummary = {
                        discovered: job.pagesDiscovered,
                        processed:  job.pagesProcessed,
                        saved:      job.pagesSaved,
                        failed:     job.pagesFailed,
                    };
                    this.localLastScrapedAt = new Date().toISOString();
                    this.$toasted.success(`Crawl complete — ${job.pagesSaved} pages saved.`);
                    this.$emit('saved', {
                        scrapeFrequency: this.localFrequency,
                        lastScrapedAt: this.localLastScrapedAt,
                    });
                    // Reload pages from the server so any cleanup deletions
                    // (URLs that vanished from the site) are reflected.
                    await this.loadPages();
                } else if (job.status === 'error') {
                    this.errorMessage = job.errorMessage || 'Crawl failed.';
                    this.$toasted.error(this.errorMessage);
                }
            },

            // ── Page editing ────────────────────────────────────────────────
            async savePage(page) {
                page._saving = true;
                try {
                    const payload = {
                        title:    page.title,
                        topic:    page.topic,
                        summary:  page.summary,
                        keyFacts: page.keyFacts.filter(f => f && f.trim()),
                        content:  page.content,
                    };
                    const response = await this.$api.put(`/kb-crawl/pages/${page.id}`, payload);
                    const updated = response.data && response.data.data;
                    if (updated) {
                        const idx = this.pages.findIndex(p => p.id === page.id);
                        if (idx !== -1) this.$set(this.pages, idx, this.normalizePage(updated));
                    }
                    this.$toasted.success('Page saved and re-indexed.');
                } catch (err) {
                    const msg = err && err.response && err.response.data && err.response.data.message;
                    this.$toasted.error(msg || 'Could not save page.');
                } finally {
                    page._saving = false;
                }
            },
            async deletePage(page) {
                if (!confirm(`Remove "${page.title || page.url}" from the chatbot's knowledge base?`)) return;
                page._deleting = true;
                try {
                    await this.$api.delete(`/kb-crawl/pages/${page.id}`);
                    this.pages = this.pages.filter(p => p.id !== page.id);
                    this.$toasted.success('Page removed.');
                } catch (err) {
                    const msg = err && err.response && err.response.data && err.response.data.message;
                    this.$toasted.error(msg || 'Could not delete page.');
                } finally {
                    page._deleting = false;
                }
            },

            // ── Misc ────────────────────────────────────────────────────────
            formatDate(s) {
                const d = new Date(s);
                return isNaN(d.getTime()) ? '' : d.toLocaleString();
            },
        },
    };
</script>

<style scoped>
    .kb-crawl-panel .section-heading {
        cursor: pointer;
        margin: 0;
        font-size: 1rem;
        font-weight: 600;
    }
    .toggle-icon {
        font-size: 1.25rem;
        line-height: 1;
    }
    .schedule-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        background: #f8f9ff;
        border: 1px solid #e5e9f2;
        border-radius: 8px;
        padding: 12px 16px;
    }
    .schedule-label { flex: 1; line-height: 1.3; }
    .schedule-select { max-width: 180px; }
    .schedule-value {
        font-weight: 600;
        color: #2e3a59;
        background: #fff;
        border: 1px solid #d8def0;
        border-radius: 6px;
        padding: 6px 14px;
        line-height: 1.3;
    }
    .kb-stat {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: #f0fbf5;
        border: 1px solid #cdebd9;
        color: #1d7a3a;
        border-radius: 999px;
        font-size: 0.8rem;
        line-height: 1;
    }
    .kb-stat strong { font-size: 0.95rem; }

    /* Status banner */
    .status-banner {
        border-radius: 10px;
        padding: 14px 18px;
        border: 1px solid #e5e9f2;
    }
    .status-banner.running {
        background: linear-gradient(90deg, #f1f7ff 0%, #eef2ff 100%);
        border-color: #c5d5f0;
    }
    .status-banner.done {
        background: #e9f8ee;
        border-color: #b6e5c7;
    }
    .status-headline {
        display: flex;
        align-items: center;
        gap: 10px;
        font-weight: 600;
        color: #2c3e80;
    }
    .status-banner.done .status-headline { color: #1d7a3a; }
    .status-banner .check {
        font-size: 1.2rem;
        background: #1d7a3a;
        color: #fff;
        border-radius: 50%;
        width: 24px;
        height: 24px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }
    .status-progress { margin-top: 6px; font-size: 0.875rem; color: #555; }
    .progress-bar-track {
        margin-top: 10px;
        background: #e0e6f5;
        border-radius: 999px;
        height: 6px;
        overflow: hidden;
    }
    .progress-bar-fill {
        background: linear-gradient(90deg, #5057c3, #6e76e3);
        height: 100%;
        transition: width 0.4s ease;
    }
    .loader-dot {
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #5057c3;
        animation: pulse 1.2s infinite ease-in-out;
    }
    @keyframes pulse {
        0%, 100% { transform: scale(1);   opacity: 1;   }
        50%      { transform: scale(1.3); opacity: 0.6; }
    }

    /* Accordion */
    .accordion-item {
        background: #fafbfc;
        transition: background-color 1.5s ease;
    }
    .accordion-item.just-arrived {
        background: #fff7d9;
    }
    .accordion-header {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 16px;
        background: transparent;
        border: none;
        text-align: left;
        cursor: pointer;
    }
    .accordion-header:hover { background: #f1f7ff; }
    .accordion-toggle {
        font-size: 1.25rem;
        width: 16px;
        text-align: center;
        color: #5057c3;
    }
    .accordion-title { flex: 1; min-width: 0; }
    .accordion-url   { word-break: break-all; }
    .accordion-body {
        background: #fff;
        border-top: 1px solid #e9ecef;
    }
    /* Keep every form control safely inside the accordion's padding box.
       Bootstrap's .form-control is width:100% but inherited box-sizing or
       textarea browser defaults can make it bleed past the right edge. */
    .accordion-body, .accordion-body * { box-sizing: border-box; }
    .accordion-body .form-control {
        width: 100%;
        max-width: 100%;
        display: block;
    }
    .accordion-body textarea.form-control {
        resize: vertical;
        min-height: 80px;
    }

    .topic-pill {
        display: inline-block;
        padding: 0.125rem 0.625rem;
        background: #eef2ff;
        color: #5057c3;
        border-radius: 999px;
        font-size: 0.7rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
    .chunks-pill {
        display: inline-block;
        padding: 0.125rem 0.625rem;
        background: #f0fbf5;
        color: #1d7a3a;
        border-radius: 999px;
        font-size: 0.7rem;
    }

    .form-row { margin-bottom: 0.75rem; }
    .form-row .form-label {
        font-size: 0.8rem;
        font-weight: 600;
        margin-bottom: 0.25rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #6c757d;
    }
    .key-fact-row {
        display: flex;
        gap: 8px;
        margin-bottom: 6px;
        align-items: center;
    }
    .key-fact-row .form-control {
        flex: 1;
        min-width: 0;     /* allow shrinking inside a flex row */
    }
    .key-fact-row .btn-icon { flex-shrink: 0; }
    .btn-icon {
        background: transparent;
        border: 1px solid #e5e9f2;
        border-radius: 6px;
        width: 36px;
        height: 36px;
        font-size: 1rem;
        color: #b71c1c;
        cursor: pointer;
        flex-shrink: 0;
    }
    .btn-icon:hover { background: #fdecea; }

    .spinner-border-sm {
        width: 0.85rem;
        height: 0.85rem;
        border-width: 2px;
    }

    /* KB search */
    .kb-search-row {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        width: 75%;
        max-width: 600px;
        margin: 0 auto;
    }
    .kb-search-input-wrapper {
        position: relative;
        width: 100%;
        box-sizing: border-box;
    }
    .kb-search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 0.9rem;
        opacity: 0.6;
        pointer-events: none;
    }
    .kb-search-input {
        height: 36px;
        padding: 4px 36px;
        font-size: 0.875rem;
        border-radius: 999px;
        background: #f8f9ff;
        border: 1px solid #e5e9f2;
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
    }
    .kb-search-input:focus {
        background: #fff;
        border-color: #5057c3;
        box-shadow: 0 0 0 3px rgba(80, 87, 195, 0.15);
        outline: none;
    }
    .kb-search-clear {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        width: 24px;
        height: 24px;
        border-radius: 50%;
        border: none;
        background: #e5e9f2;
        color: #555;
        font-size: 1rem;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .kb-search-clear:hover {
        background: #d4dbeb;
    }
    .kb-search-count {
        white-space: nowrap;
    }
</style>
